import * as z from "zod";
import { emailAddress } from "../email-address";

// Identity operation inputs (ADR-014, ADR-024): the same schemas validate the gateway route and the RPC method.

/** Actor of a signed-in user, from the session's userId: called by the gateways only. */
export const buildActorInput = z.object({
  userId: z.uuid(),
});
export type BuildActorInput = z.input<typeof buildActorInput>;

/**
 * Backstage login callback: claims of the Logto ID token (ADR-024, point 8). Without an email only an account already
 * linked to the subject can match.
 */
export const resolveInternalUserInput = z.object({
  logtoSub: z.string().min(1).max(64),
  email: emailAddress.nullable(),
  emailVerified: z.boolean(),
});
export type ResolveInternalUserInput = z.input<typeof resolveInternalUserInput>;

/**
 * `/auth/login` of the gateways: where to go back after the login. A path of the same origin only (no open
 * redirect): never `//host` or `/\host`, and no whitespace or control characters, which browsers drop
 * (`/<tab>/host` becomes `//host`).
 */
export const loginInput = z.object({
  returnTo: z
    .string()
    .max(2000)
    .regex(/^\/(?![/\\])[^\s\\\p{Cc}]*$/u)
    .default("/"),
});
export type LoginInput = z.input<typeof loginInput>;

/**
 * Why a login navigation failed (ADR-046): the codes of `/auth/login` and `/auth/callback` (the gateway's, then the
 * refusals of `resolveInternalUser`, ADR-034 point 7), each with a text on the apps' sign-in page.
 */
export const loginErrorCodes = [
  "login_unavailable",
  "login_expired",
  "login_failed",
  "account_not_found",
  "email_not_verified",
  "account_linked_elsewhere",
  "not_internal",
  "account_inactive",
  // an internal user without a role of the app of the gateway (ADR-294): Guestlist only, Backstage opens to all of them
  "app_not_allowed",
  "internal_error",
] as const;
export type LoginErrorCode = (typeof loginErrorCodes)[number];

/**
 * Query of the apps' `/sign-in` page, where a gateway redirects a login navigation that failed (P2.7). It is a URL
 * anyone can edit: a malformed value falls back instead of failing, and `returnTo` follows the rules of `loginInput`.
 */
export const signInPageInput = z.object({
  error: z.enum(loginErrorCodes).optional().catch("internal_error"),
  requestId: z.string().max(100).optional().catch(undefined),
  returnTo: loginInput.shape.returnTo.catch("/"),
});

/** What `platform.access_log` records (FR-M1-07): who signed in and out, and what was refused to whom. */
export const accessEvents = [
  "login",
  "logout",
  "access_denied",
  // the MCP server and its authorization server (ADR-263): a sign-in refused or a consent, a pair of tokens given or
  // rotated, a connection revoked by its client, a call of a tool
  "mcp_sign_in",
  "mcp_consent",
  "token_issued",
  "token_refreshed",
  "token_revoked",
  "tool_call",
] as const;

/** The most characters of the arguments of a call of a tool the access log keeps. */
export const ACCESS_DETAILS_MAX = 2000;

/**
 * A line of the access log, written by the gateways after the response. `resource` is what was asked: the route
 * pattern of a refused request (never the path with its ids), or the code of a refused login.
 */
export const recordAccessInput = z.object({
  event: z.enum(accessEvents),
  success: z.boolean(),
  /** Null when the caller is not a user: a machine token, or a login that matched no account. */
  userId: z.uuid().nullable(),
  resource: z.string().max(300).nullable(),
  /** A header that is not an address is dropped, not refused: the line matters more than the field. */
  ipAddress: z.union([z.ipv4(), z.ipv6()]).nullable().catch(null),
  /** Cut, not refused, for the same reason. */
  userAgent: z
    .string()
    .transform((agent) => agent.slice(0, 500))
    .nullable(),
  /** The token or connection of the call (ADR-263): never the token itself. */
  delegatedAccessId: z.uuid().nullable().optional(),
  /**
   * A call of a tool of the MCP server (spec v0.3 §8.O, ADR-264): its arguments as JSON, cut, and its outcome (`ok` or
   * the code of the error). Cut, not refused, like the agent.
   */
  details: z
    .object({
      arguments: z.string().transform((text) => text.slice(0, ACCESS_DETAILS_MAX)),
      outcome: z.string().max(100),
    })
    .nullable()
    .optional(),
});
export type RecordAccessInput = z.input<typeof recordAccessInput>;

export const getMeInput = z.object({});
export type GetMeInput = z.input<typeof getMeInput>;

/** The signed-in user (`party.user_account`): the profile fields a field rule can hide are optional. */
export const meDto = z
  .object({
    id: z.uuid(),
    email: z.string(),
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    locale: z.string().nullable().optional(),
    organization: z.object({ id: z.uuid(), name: z.string(), type: z.string() }),
    roles: z.array(z.string()),
    permissions: z.array(z.string()),
  })
  .meta({ id: "Me" });

/**
 * `GET /auth/me` of Atelier: the user, and whether the session is limited, because a role requires a second factor the
 * session has not proved (ADR-151, ADR-157): it reaches only the routes that add one.
 */
export const externalMeDto = meDto.extend({ mfaEnrollmentRequired: z.boolean() }).meta({ id: "ExternalMe" });

/** `POST /auth/logout`: the session is already gone; the app opens `logoutUrl` to close the Logto one too. */
export const logoutDto = z.object({ logoutUrl: z.url() });

/**
 * KV key holding a user's `credential_version` (ADR-032): written by identity, compared with the session by the
 * gateways. Written only from code running at FRA (trap 12).
 */
export const credentialVersionKey = (userId: string) => `cv:${userId}`;

export * from "./assistant-access";
export * from "./auth";
export * from "./delegated-access";
export * from "./enums";
export * from "./events";
export * from "./mfa";
export * from "./mfa-reset";
export * from "./oauth";
export * from "./prepared-action";
